import React, { createContext, useContext, useState, useCallback } from 'react';
import { CheckCircle, AlertCircle, AlertTriangle, X } from 'lucide-react';
import { AnimatePresence, motion } from 'framer-motion';

// ─── Contexte ───────────────────────────────────────────────────────────────
const ToastContext = createContext(null);

export const useToast = () => {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast doit être utilisé dans <ToastProvider>');
  return ctx;
};

// ─── Provider ───────────────────────────────────────────────────────────────
let nextId = 0;

export const ToastProvider = ({ children }) => {
  const [toasts, setToasts] = useState([]);

  const showToast = useCallback((message, type = 'success', duration = 4000) => {
    const id = ++nextId;
    setToasts(prev => [...prev, { id, message, type }]);
    setTimeout(() => setToasts(prev => prev.filter(t => t.id !== id)), duration);
  }, []);

  const dismiss = useCallback((id) => {
    setToasts(prev => prev.filter(t => t.id !== id));
  }, []);

  return (
    <ToastContext.Provider value={showToast}>
      {children}

      {/* Conteneur global - toujours au-dessus de tout */}
      <div className="fixed top-5 right-5 z-[9999] flex flex-col gap-2.5 w-full max-w-xs pointer-events-none">
        <AnimatePresence initial={false}>
          {toasts.map(toast => (
            <ToastItem key={toast.id} toast={toast} onDismiss={() => dismiss(toast.id)} />
          ))}
        </AnimatePresence>
      </div>
    </ToastContext.Provider>
  );
};

// ─── Composant visuel ────────────────────────────────────────────────────────
const STYLES = {
  success: {
    icon: CheckCircle,
    wrapper: 'bg-emerald-600 text-white shadow-emerald-200',
  },
  error: {
    icon: AlertCircle,
    wrapper: 'bg-red-600 text-white shadow-red-200',
  },
  warning: {
    icon: AlertTriangle,
    wrapper: 'bg-amber-500 text-white shadow-amber-200',
  },
};

const ToastItem = ({ toast, onDismiss }) => {
  const cfg = STYLES[toast.type] || STYLES.success;
  const Icon = cfg.icon;

  return (
    <motion.div
      initial={{ opacity: 0, x: 48, scale: 0.94 }}
      animate={{ opacity: 1, x: 0,  scale: 1    }}
      exit={{    opacity: 0, x: 48, scale: 0.9  }}
      transition={{ duration: 0.22, ease: 'easeOut' }}
      className={`
        pointer-events-auto flex items-center gap-3
        px-4 py-3.5 rounded-2xl shadow-xl
        ${cfg.wrapper}
      `}
    >
      <Icon size={18} className="shrink-0" />
      <p className="text-sm font-semibold flex-1 leading-snug">{toast.message}</p>
      <button
        onClick={onDismiss}
        aria-label="Fermer"
        className="shrink-0 opacity-70 hover:opacity-100 transition-opacity ml-1"
      >
        <X size={15} />
      </button>
    </motion.div>
  );
};

export default ToastProvider;
